/**
 * SVG Image Viewer with Zoom and Pan Controls
 * 
 * Displays an SVG from a URL with:
 * - Zoom controls (+, -, reset, fit-to-view)
 * - Mouse wheel zoom
 * - Pan/drag support
 * - Auto zoom-to-fit on initial load
 */

import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Spinner } from '@/components/ui';
import { logger } from '../../utils/logger';

interface SVGImageViewerProps {
  /** URL of the SVG to display */
  svgUrl: string;
  /** Optional title for the diagram */
  title?: string;
  /** Minimum zoom level (default 0.1) */
  minZoom?: number;
  /** Maximum zoom level (default 5) */
  maxZoom?: number;
  /** Auto zoom to fit on load (default true) */
  autoFitOnLoad?: boolean;
}

interface ViewTransform {
  x: number;
  y: number;
  scale: number;
}

export const SVGImageViewer: React.FC<SVGImageViewerProps> = ({
  svgUrl,
  title,
  minZoom = 0.1,
  maxZoom = 5,
  autoFitOnLoad = true,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  
  const [transform, setTransform] = useState<ViewTransform>({ x: 0, y: 0, scale: 1 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  
  // Fit the image to the container (zoom to extents)
  const fitToView = useCallback(() => {
    if (!containerRef.current || !imageSize.width || !imageSize.height) return;
    
    const container = containerRef.current;
    const containerWidth = container.clientWidth - 32; // padding
    const containerHeight = container.clientHeight - 32;
    
    const scaleX = containerWidth / imageSize.width;
    const scaleY = containerHeight / imageSize.height;
    // Zoom to fit - allow both zooming in and out, capped at maxZoom
    const scale = Math.min(scaleX, scaleY, maxZoom);
    
    // Center the image
    const scaledWidth = imageSize.width * scale;
    const scaledHeight = imageSize.height * scale;
    const x = (containerWidth - scaledWidth) / 2 + 16;
    const y = (containerHeight - scaledHeight) / 2 + 16;
    
    setTransform({ x, y, scale });
  }, [imageSize, maxZoom]);
  
  // Handle image load - get dimensions and optionally fit to view
  const handleImageLoad = useCallback(async () => {
    if (!imageRef.current) return;
    
    const img = imageRef.current;
    let width = img.naturalWidth;
    let height = img.naturalHeight;
    
    // For SVGs, naturalWidth/Height might be 0 or small if no explicit dimensions
    // Try to fetch and parse the SVG viewBox for accurate dimensions
    if ((width === 0 || height === 0 || width < 100) && svgUrl) {
      try {
        const response = await fetch(svgUrl);
        const svgText = await response.text();
        
        // Try to parse viewBox
        const viewBoxMatch = svgText.match(/viewBox=["']([^"']+)["']/);
        if (viewBoxMatch) {
          const [, , vbWidth, vbHeight] = viewBoxMatch[1].split(/\s+/).map(Number);
          if (vbWidth > 0 && vbHeight > 0) {
            width = vbWidth;
            height = vbHeight;
          }
        }
        
        // Also try explicit width/height attributes
        if (width === 0 || height === 0) {
          const widthMatch = svgText.match(/\bwidth=["'](\d+(?:\.\d+)?)/);
          const heightMatch = svgText.match(/\bheight=["'](\d+(?:\.\d+)?)/);
          if (widthMatch) width = parseFloat(widthMatch[1]);
          if (heightMatch) height = parseFloat(heightMatch[1]);
        }
      } catch (err) {
        logger.warn('[SVGImageViewer] Failed to fetch SVG for dimension parsing:', err);
      }
    }
    
    // Fallback to reasonable defaults if still no dimensions
    if (width === 0) width = 800;
    if (height === 0) height = 600;
    
    logger.debug('[SVGImageViewer] Image dimensions:', { width, height, natural: { w: img.naturalWidth, h: img.naturalHeight } });
    setImageSize({ width, height });
    setImageLoaded(true);
  }, [svgUrl]);
  
  // Reset state when URL changes
  useEffect(() => {
    logger.debug('[SVGImageViewer] URL changed, resetting state. URL:', svgUrl?.substring(0, 100) + '...');
    setImageLoaded(false);
    setImageError(null);
    setImageSize({ width: 0, height: 0 });
  }, [svgUrl]);
  
  // Auto fit when image loads
  useEffect(() => {
    if (imageLoaded && autoFitOnLoad && imageSize.width > 0) {
      fitToView();
    }
  }, [imageLoaded, autoFitOnLoad, imageSize, fitToView]);
  
  // Handle wheel zoom - uses native event for passive: false
  const handleWheel = useCallback((e: WheelEvent) => {
    e.preventDefault();
    
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    
    const scaleBy = 1.1;
    const newScale = e.deltaY < 0 
      ? Math.min(transform.scale * scaleBy, maxZoom)
      : Math.max(transform.scale / scaleBy, minZoom);
    
    // Zoom toward mouse position
    const scaleDiff = newScale - transform.scale;
    const newX = transform.x - (mouseX - transform.x) * (scaleDiff / transform.scale);
    const newY = transform.y - (mouseY - transform.y) * (scaleDiff / transform.scale);
    
    setTransform({ x: newX, y: newY, scale: newScale });
  }, [transform, minZoom, maxZoom]);
  
  // Attach wheel listener with passive: false to allow preventDefault
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    
    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);
  
  // Handle pan start
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button === 0 || e.button === 1) { // Left or middle button
      setIsPanning(true);
      setPanStart({ x: e.clientX - transform.x, y: e.clientY - transform.y });
    }
  }, [transform]);
  
  // Handle pan move
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isPanning) return;
    setTransform(prev => ({
      ...prev,
      x: e.clientX - panStart.x,
      y: e.clientY - panStart.y,
    }));
  }, [isPanning, panStart]);
  
  // Handle pan end
  const handleMouseUp = useCallback(() => {
    setIsPanning(false);
  }, []);
  
  // Zoom controls
  const zoomIn = useCallback(() => {
    setTransform(prev => ({ ...prev, scale: Math.min(prev.scale * 1.25, maxZoom) }));
  }, [maxZoom]);
  
  const zoomOut = useCallback(() => {
    setTransform(prev => ({ ...prev, scale: Math.max(prev.scale / 1.25, minZoom) }));
  }, [minZoom]);
  
  const resetView = useCallback(() => {
    setTransform({ x: 0, y: 0, scale: 1 });
  }, []);
  
  return (
    <div className="svg-image-viewer flex flex-col h-full">
      {/* Viewer container */}
      <div
        ref={containerRef}
        className="flex-1 relative overflow-hidden rounded-lg border border-border bg-muted"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        {/* SVG Image with transform */}
        <img
          ref={imageRef}
          src={svgUrl}
          alt={title || 'SVG Diagram'}
          crossOrigin="anonymous"
          onLoad={handleImageLoad}
          onError={() => {
            logger.error('[SVGImageViewer] Failed to load image');
            setImageError('Failed to load diagram image. The URL may have expired.');
          }}
          style={{
            position: 'absolute',
            transformOrigin: '0 0',
            transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
            cursor: isPanning ? 'grabbing' : 'grab',
            maxWidth: 'none', // Allow image to be larger than container
          }}
          draggable={false}
        />
        
        {/* Error display */}
        {imageError && (
          <div className="absolute inset-0 flex items-center justify-center bg-destructive-light">
            <div className="text-center text-destructive">
              <div className="text-4xl mb-2">⚠️</div>
              <div className="text-sm">{imageError}</div>
            </div>
          </div>
        )}
        
        {/* Zoom controls */}
        <div className="absolute bottom-4 right-4 flex flex-col gap-1 bg-card border border-border rounded-lg shadow-lg overflow-hidden">
          <button
            className="px-3 py-2 text-lg font-bold text-foreground hover:bg-muted transition-colors"
            onClick={zoomIn}
            title="Zoom In"
          >
            +
          </button>
          <div className="px-3 py-1 text-xs text-muted-foreground text-center border-y border-border bg-muted">
            {Math.round(transform.scale * 100)}%
          </div>
          <button
            className="px-3 py-2 text-lg font-bold text-foreground hover:bg-muted transition-colors"
            onClick={zoomOut}
            title="Zoom Out"
          >
            −
          </button>
          <div className="border-t border-border" />
          <button
            className="px-3 py-2 text-xs font-medium text-foreground hover:bg-muted transition-colors"
            onClick={fitToView}
            title="Fit to View"
          >
            Fit
          </button>
          <button
            className="px-3 py-2 text-xs font-medium text-foreground hover:bg-muted transition-colors"
            onClick={resetView}
            title="Reset to 100%"
          >
            1:1
          </button>
        </div>
        
        {/* Loading indicator */}
        {!imageLoaded && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex flex-col items-center gap-2 text-muted-foreground">
              <Spinner size="xl" />
              <span className="text-sm">Loading diagram...</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SVGImageViewer;
